// ============================================================
// TtrClaimRouteModal — 노선 클레임 카드 선택 모달
//   SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md §2-5 / §4-2 ClaimRoute
//   SEQ=10146 — 색 카드 N장 + 와일드 X장 조합 입력 + 클라 1차 비용 정합 검증
//                (서버 권위 — TtrActionError 발생 시 토스트로 fallback)
//
//   규칙 정리:
//     - 색 노선: colorUsed = 노선 색, count(colorUsed) + wildCount = length
//     - 회색 노선: colorUsed = 본인이 사용한 단색 (8색 중 1) 또는 'WILD', wildCount = 와일드 사용
//       · 단색 + 와일드만 허용 (다른 두 색 섞기 금지)
//       · 전부 와일드면 colorUsed = 'WILD', wildCount = length
// ============================================================
import React, { useMemo, useState } from 'react'
import { TtrRoute, TtrMyHandSummary } from '../store/ticket'

export interface TtrClaimRouteModalProps {
  route:      TtrRoute
  cityAName:  string
  cityBName:  string
  myHand:     TtrMyHandSummary
  /** colorUsed: 색 노선 = route.color, 회색 노선 = 사용자가 선택한 색('WILD' 가능)
   *  wildCount: 0~length */
  onConfirm:  (colorUsed: string, wildCount: number) => void
  onCancel:   () => void
}

const TRAIN_COLORS = ['RED', 'ORANGE', 'YELLOW', 'GREEN', 'BLUE', 'PINK', 'WHITE', 'BLACK'] as const

const TtrClaimRouteModal: React.FC<TtrClaimRouteModalProps> = ({
  route,
  cityAName,
  cityBName,
  myHand,
  onConfirm,
  onCancel,
}) => {
  const isGray = route.color === 'GRAY'

  // 회색 노선은 사용자 단색 선택 필요 (기본: 최다 보유 색)
  const defaultGrayColor: string = useMemo(() => {
    let best = 'RED'
    let bestCount = -1
    TRAIN_COLORS.forEach(c => {
      const cnt = myHand[c] ?? 0
      if (cnt > bestCount) {
        bestCount = cnt
        best = c
      }
    })
    return best
  }, [myHand])

  const [grayChoiceColor, setGrayChoiceColor] = useState<string>(defaultGrayColor)
  const [wildCount, setWildCount]             = useState<number>(0)

  // 실제 사용될 색 (색 노선이면 노선 색, 회색이면 사용자 선택)
  const colorUsed = isGray ? grayChoiceColor : route.color
  // 전부 와일드 케이스 — 회색 노선 한정
  const useAllWild = isGray && wildCount === route.length

  // 정합 검증 (클라 1차 — 서버 권위)
  const colorAvail = myHand[colorUsed] ?? 0
  const wildAvail  = myHand['WILD']    ?? 0
  const colorNeed  = useAllWild ? 0 : (route.length - wildCount)
  const wildNeed   = wildCount

  const enoughColor = useAllWild ? true : colorAvail >= colorNeed
  const enoughWild  = wildAvail  >= wildNeed
  const validRange  = wildCount >= 0 && wildCount <= route.length
  const canConfirm  = enoughColor && enoughWild && validRange

  function handleConfirm() {
    if (!canConfirm) return
    const finalColor = useAllWild ? 'WILD' : colorUsed
    onConfirm(finalColor, wildCount)
  }

  return (
    <div className="ttr-modal-overlay" onClick={onCancel}>
      <div className="ttr-modal ttr-claim-route-modal" onClick={e => e.stopPropagation()}>
        <h2>노선 클레임</h2>

        <div className="ttr-claim-info">
          <p>
            <strong>{cityAName}</strong> ↔ <strong>{cityBName}</strong>
          </p>
          <p>
            색: <span className={`ttr-color-chip ttr-color-${route.color.toLowerCase()}`}>{route.color}</span>
            {' · '}길이: {route.length}
            {route.isDouble && ' · 더블 (lane ' + route.laneNo + ')'}
          </p>
        </div>

        {/* 회색 노선 — 사용자 단색 선택 */}
        {isGray && (
          <div className="ttr-claim-gray-pick">
            <p>회색 노선 — 사용할 색 (단색):</p>
            <div className="ttr-color-row">
              {TRAIN_COLORS.map(c => (
                <button
                  key={c}
                  className={
                    'ttr-color-btn ttr-color-' + c.toLowerCase() +
                    (c === grayChoiceColor ? ' selected' : '')
                  }
                  disabled={(myHand[c] ?? 0) === 0 && wildCount !== route.length}
                  onClick={() => setGrayChoiceColor(c)}
                  title={`${c} (보유 ${myHand[c] ?? 0}장)`}
                >
                  {c} ({myHand[c] ?? 0})
                </button>
              ))}
            </div>
            <p style={{ fontSize: 12, color: '#666' }}>
              또는 와일드 {route.length}장 단독 (회색 노선 한정 허용)
            </p>
          </div>
        )}

        {/* 와일드 사용 수 */}
        <div className="ttr-claim-wild">
          <p>와일드 사용 수 (보유 {wildAvail}장):</p>
          <input
            type="range"
            min={0}
            max={route.length}
            value={wildCount}
            onChange={e => setWildCount(Number(e.target.value))}
          />
          <span>{wildCount} / {route.length}</span>
        </div>

        {/* 비용 요약 */}
        <div className="ttr-claim-cost">
          <p>
            소비 카드:
            {useAllWild
              ? ` 와일드 ${route.length}장`
              : ` ${colorUsed} ${colorNeed}장 + 와일드 ${wildNeed}장`}
            {' · 기차 모형 '}{route.length}개
          </p>
        </div>

        {/* 정합 경고 */}
        {!canConfirm && (
          <p className="ttr-claim-warn">
            {!enoughColor && <>· {colorUsed} 색 카드 부족 (보유 {colorAvail}, 필요 {colorNeed})<br/></>}
            {!enoughWild  && <>· 와일드 부족 (보유 {wildAvail}, 필요 {wildNeed})<br/></>}
            {!validRange  && <>· 와일드 수가 노선 길이 범위를 벗어남</>}
          </p>
        )}

        <div className="ttr-modal-actions">
          <button onClick={onCancel}>취소</button>
          <button onClick={handleConfirm} disabled={!canConfirm}>
            점유 확정
          </button>
        </div>
      </div>
    </div>
  )
}

export default TtrClaimRouteModal
